import React from 'react';
import { SparkleSolidIcon } from '@mentimeter/ragnar-visuals';
import { ErrorBoundary } from '@mentimeter/errors/ErrorBoundary';
import type { ResponseCluster } from '@mentimeter/http-clients';
import type { GroupShapeMap } from '@mentimeter/slides-presentation-ui/groups/group-shapes';
import { GroupingLoader } from '@mentimeter/slides-presentation-ui/groups/loader';
import {
  GroupingError,
  type GroupingErrorType,
} from '@mentimeter/slides-presentation-ui/groups/error';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useTranslations } from 'next-intl';
import { UNGROUPED_CLUSTER_ID } from '../../../groups';
import { GroupsOverviewCards } from '../GroupsOverviewCards';
import { GroupDetailViewDataHandler } from '../group-detail-view/GroupDetailViewDataHandler';
import { AnimatedSubheading } from '../motion/AnimatedSubheading';
import { OpenEndedTitleDataHandler } from '../../ui/OpenEndedTitleDataHandler';
import { NewResponsesNotification } from './NewResponsesNotification';

export interface GroupsWithoutVotesViewProps {
  shouldHideResponses: boolean;
  error?: GroupingErrorType | undefined;
  isLoading: boolean;
  groups: ResponseCluster[];
  groupShapeMap: GroupShapeMap;
  onRegenerate: () => void;
  onBackToResponses: () => void;
  clusteringId: string | undefined;
}
export const GroupsWithoutVotesView = ({
  shouldHideResponses,
  error,
  isLoading,
  groups,
  groupShapeMap,
  onRegenerate,
  onBackToResponses,
  clusteringId,
}: GroupsWithoutVotesViewProps) => {
  const translate = useTranslations('presenting.legacy');

  const newResponsesGroup = groups.find(
    (group) => group.id === UNGROUPED_CLUSTER_ID,
  );

  const numberOfGroups = groups.length;

  const subheadingText = translate(
    numberOfGroups === 1 ? 'message.group_found' : 'message.groups_found',
    {
      nrOfGroups: `${numberOfGroups}`,
    },
  );

  const shouldShowClusterIndicator = numberOfGroups > 0 && !shouldHideResponses;

  return (
    <ErrorBoundary
      feature="slides"
      fallback={
        <GroupingError
          buttonText="Reload"
          errorType="something-went-wrong"
          onClick={() => window.location.reload()}
        />
      }
    >
      <Box width="100%" height="100%">
        {error ? (
          <GroupingError
            onGoBack={onBackToResponses}
            buttonText={translate('open.try_again')}
            onClick={onRegenerate}
            errorType={error}
          />
        ) : isLoading ? (
          <GroupingLoader />
        ) : !groups.length ? (
          <Text>{translate('open.no_groups')}</Text>
        ) : (
          <Box width="100%" height="100%" flex="1 1 auto" overflow="hidden">
            <GroupDetailViewDataHandler
              groups={groups}
              groupShapeMap={groupShapeMap}
              shouldShowVotes={false}
              clusteringId={clusteringId}
            />
            <OpenEndedTitleDataHandler>
              <AnimatedSubheading shouldShow={shouldShowClusterIndicator}>
                <Box mr={3}>
                  <SparkleSolidIcon size={3} color="textWeak" />
                </Box>
                <Text
                  as="h2"
                  color="textWeak"
                  fontSize="200"
                  mt="space1"
                  lineHeight="none"
                >
                  {subheadingText}
                </Text>
              </AnimatedSubheading>
            </OpenEndedTitleDataHandler>

            <Box flex="1 1 auto" width="100%" gap={4} overflow="auto">
              {!shouldHideResponses && (
                <GroupsOverviewCards
                  groups={groups}
                  groupShapeMap={groupShapeMap}
                />
              )}
            </Box>
            <NewResponsesNotification
              newResponses={newResponsesGroup?.responses}
            />
          </Box>
        )}
      </Box>
    </ErrorBoundary>
  );
};
